import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import Global from '../../../../Services/Common/Global';
import useSearchMutaion from '../../../../hooks/apis/useSearchMutaion';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';
import useDeleteMutation from '../../../../hooks/apis/useDeleteMutaion';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../util';
import { AUT_BASIC_Schema } from './AUT_BASIC_Schema';

export const normalizeAutBasicBatchResults = (res) => {
  let list = [];
  if (Array.isArray(res)) list = res;
  else if (Array.isArray(res?.data)) list = res.data;
  else if (Array.isArray(res?.Data)) list = res.Data;
  else if (res && typeof res === 'object') {
    if ('isSuccess' in res || 'IsSuccess' in res) {
      list = [{ ...res }];
    }
  }
  return list.map((item) => ({
    ...item,
    isSuccess: item.isSuccess ?? item.IsSuccess ?? false,
    message:
      item.message ?? item.Message ?? item.errorMsg ?? item.ErrorMsg ?? '',
  }));
};

const unwrapAutList = (raw) => {
  if (!raw || raw.isSuccess === false || raw.IsSuccess === false) return [];
  const arr = Array.isArray(raw.data)
    ? raw.data
    : Array.isArray(raw.Data)
      ? raw.Data
      : Array.isArray(raw)
        ? raw
        : [];
  if (arr.length > 0 && arr[0]?.type === 'Error') return [];
  return arr;
};

export const normalizeAutBasicR1Row = (r, rowIndex = 0) => ({
  no:
    r.no ??
    r.No ??
    `aut1:${r.PRO_ID ?? r.proId ?? ''}:${r.PRO_DTL ?? r.proDtl ?? ''}:${rowIndex}`,
  AUTH_CD: r.AUTH_CD ?? r.authCd ?? '',
  PRO_ID: r.PRO_ID ?? r.proId ?? '',
  PRO_CONTENT: r.PRO_CONTENT ?? r.proContent ?? '',
  PRO_DTL: r.PRO_DTL ?? r.proDtl ?? '',
  PRO_DTL_NM: r.PRO_DTL_NM ?? r.proDtlNm ?? '',
  SAVE_YN: r.SAVE_YN ?? r.saveYn ?? 'N',
  DELETE_YN: r.DELETE_YN ?? r.deleteYn ?? 'N',
  PRINT_YN: r.PRINT_YN ?? r.printYn ?? 'N',
});

export const normalizeAutBasicR2Row = (r, rowIndex = 0) => ({
  no: r.no ?? r.No ?? `aut2:${r.PRO_ID ?? r.proId ?? ''}:${rowIndex}`,
  PRO_ID: r.PRO_ID ?? r.proId ?? '',
  PRO_CONTENT: r.PRO_CONTENT ?? r.proContent ?? '',
  MENU_ID: r.MENU_ID ?? r.menuId ?? '',
});

/** Utility.GetCodeInfo — SP_GetCode 대체 */
export const useSearchCode = (options = {}) => {
  const { onSuccess } = options;

  const query = useQuery({
    queryKey: ['Utility', 'getCodeInfo', 'AUT_BASIC'],
    queryFn: () => apiRequest('post', Global.urls.Utility.getCodeInfo),
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess) return;

    handleQuerySuccess(query.data, {
      onSuccess: async (rows) => {
        await onSuccess?.(rows);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- query 성공 시에만 onSuccess 호출
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

/** 권한별 메뉴상세 GetAutBasicR1 */
export const useSearch1 = (options = {}) => {
  const { onSuccess, onErrorRow, authCd } = options;

  const query = useQuery({
    queryKey: ['Auth', 'getAutBasicR1', 'AUT_BASIC', authCd],
    queryFn: () => {
      const params = mapParamsBySchema(AUT_BASIC_Schema.mapper.search1, {
        authCd,
        params: '',
      });
      return apiRequest('post', Global.urls.Auth.getAutBasicR1, params);
    },
    enabled: String(authCd ?? '').length > 0,
  });

  useEffect(() => {
    if (!query.isSuccess) return;
    const raw = query.data ?? {};
    if (raw.isSuccess === false || raw.IsSuccess === false) {
      handleQuerySuccess({ data: [] }, { onSuccess });
      return;
    }
    const arr = unwrapAutList(raw);
    if (arr.length > 0 && arr[0]?.type === 'Error') {
      onErrorRow?.(arr[0]);
      handleQuerySuccess({ data: [] }, { onSuccess });
      return;
    }
    const mapped = arr.map((row, idx) => normalizeAutBasicR1Row(row, idx));
    handleQuerySuccess({ data: mapped }, { onSuccess });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- query 성공 시에만 onSuccess 호출
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

/** 프로그램 목록(모달) GetAutBasicR2 */
export const useSearch2 = (options = {}) => {
  return useSearchMutaion(async (params) => {
    const apiParams = mapParamsBySchema(
      AUT_BASIC_Schema.mapper.search2,
      params || {}
    );
    const raw = await apiRequest(
      'post',
      Global.urls.Auth.getAutBasicR2,
      apiParams
    );
    const arr = unwrapAutList(raw);
    const mapped = arr.map((row, idx) => normalizeAutBasicR2Row(row, idx));
    return { data: mapped };
  }, options);
};

/** 복사용 — 동일 R1, 다른 AUTH_CD */
export const useSearchAutR1Mutation = (options = {}) => {
  return useSearchMutaion(async (params) => {
    const apiParams = mapParamsBySchema(
      AUT_BASIC_Schema.mapper.search1,
      params || {}
    );
    const raw = await apiRequest(
      'post',
      Global.urls.Auth.getAutBasicR1,
      apiParams
    );
    const rawArr = Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.Data)
        ? raw.Data
        : Array.isArray(raw)
          ? raw
          : [];
    if (rawArr.length > 0 && rawArr[0]?.type === 'Error') {
      throw new Error(rawArr[0].message || '조회 중 오류가 발생했습니다.');
    }
    const arr = unwrapAutList(raw);
    const mapped = arr.map((row, idx) => normalizeAutBasicR1Row(row, idx));
    return { data: mapped };
  }, options);
};

export const useSave1 = (context = {}, options = {}) => {
  return useSaveMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, AUT_BASIC_Schema.mapper.save1, context);
    const res = await apiRequest(
      'post1',
      Global.urls.Auth.saveAutBasicU1,
      payload
    );
    return normalizeAutBasicBatchResults(res);
  }, options);
};

export const useDelete1 = (context = {}, options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(
      rows,
      AUT_BASIC_Schema.mapper.delete1,
      context
    );
    const res = await apiRequest(
      'post1',
      Global.urls.Auth.deleteAutBasicD1,
      payload
    );
    return normalizeAutBasicBatchResults(res);
  }, options);
};
